import React, { useState, useEffect, useCallback } from 'react';

const photos = [
  { src: '/images/gallery/chimney-hills-early-phase.png', caption: 'Chimney Hills - Early Phase', category: 'Field Work' },
  { src: '/images/gallery/chimney-hills-dense-growth.png', caption: 'Chimney Hills - Dense Growth', category: 'Field Work' },
  { src: '/images/gallery/before-barren-land.png', caption: 'Before - Barren Land', category: 'Before/After' },
  { src: '/images/gallery/after-dense-canopy.png', caption: 'After - Dense Canopy', category: 'Before/After' },
  { src: '/images/gallery/community-plantation-action.png', caption: 'Community Plantation Action', category: 'Community' },
  ...Array.from({ length: 7 }, (_, i) => ({
    src: `/images/gallery/field-photo-${String(i + 1).padStart(2, '0')}.jpeg`,
    caption: `Field Photo ${i + 1}`,
    category: 'Field Work',
  })),
  ...Array.from({ length: 7 }, (_, i) => ({
    src: `/images/gallery/field-photo-${String(i + 8).padStart(2, '0')}.jpeg`,
    caption: `Field Photo ${i + 8}`,
    category: 'Community',
  })),
  ...Array.from({ length: 7 }, (_, i) => ({
    src: `/images/gallery/field-photo-${String(i + 15).padStart(2, '0')}.jpeg`,
    caption: `Field Photo ${i + 15}`,
    category: 'Field Work',
  })),
];

const videos = Array.from({ length: 4 }, (_, i) => ({
  src: `/images/gallery/field-video-${String(i + 1).padStart(2, '0')}.mp4`,
  caption: `Field Video ${i + 1}`,
}));

const PHOTOS_PER_PAGE = 12;

export default function GalleryPage() {
  const [activeTab, setActiveTab] = useState('photos');
  const [lightbox, setLightbox] = useState(null);
  const [photoCategory, setPhotoCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(PHOTOS_PER_PAGE);

  useEffect(() => {
    document.title = 'Gallery | Haritanjali';
  }, []);

  const filteredPhotos = photoCategory === 'All'
    ? photos
    : photos.filter(p => p.category === photoCategory);

  const displayedPhotos = filteredPhotos.slice(0, visibleCount);

  // Reset visible count when category changes
  const handleCategoryChange = useCallback((cat) => {
    setPhotoCategory(cat);
    setVisibleCount(PHOTOS_PER_PAGE);
  }, []);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox(prev => (prev + 1) % filteredPhotos.length);
      if (e.key === 'ArrowLeft') setLightbox(prev => (prev - 1 + filteredPhotos.length) % filteredPhotos.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, filteredPhotos.length]);

  const openLightbox = useCallback((i) => setLightbox(i), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  return (
    <>
      {/* Hero */}
      <section style={{
        position: 'relative',
        height: '45vh',
        minHeight: 340,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/images/gallery/community-plantation-action.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(27,94,32,0.7) 100%)',
        }} />
        <div style={{
          position: 'relative', zIndex: 2, textAlign: 'center',
          color: '#fff', maxWidth: 700, padding: '0 20px',
        }}>
          <p style={{
            fontSize: 14, letterSpacing: 4, textTransform: 'uppercase',
            marginBottom: 16, opacity: 0.85, fontWeight: 500,
          }}>
            See It In Action
          </p>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 800, lineHeight: 1.12,
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}>
            Gallery
          </h1>
          <p style={{
            fontSize: 'clamp(15px, 2vw, 19px)',
            opacity: 0.9, maxWidth: 550, margin: '16px auto 0',
            lineHeight: 1.7,
          }}>
            Real images and videos from our plantation drives across India
          </p>
        </div>
      </section>

      {/* Tab switcher + content */}
      <section style={{ padding: '50px 20px 80px', background: '#f5f7f5' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Main tabs: Photos / Videos */}
          <div style={{
            display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24,
          }}>
            {['photos', 'videos'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 32px',
                  borderRadius: 30,
                  border: 'none',
                  background: activeTab === tab
                    ? 'linear-gradient(135deg, #2e7d32, #4caf50)'
                    : '#fff',
                  color: activeTab === tab ? '#fff' : '#555',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: activeTab === tab
                    ? '0 4px 16px rgba(46,125,50,0.3)'
                    : '0 2px 8px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s',
                  textTransform: 'capitalize',
                }}
              >
                {tab === 'photos' ? `Photos (${filteredPhotos.length})` : `Videos (${videos.length})`}
              </button>
            ))}
          </div>

          {/* Photo category filter pills */}
          {activeTab === 'photos' && (
            <div style={{
              display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 32, flexWrap: 'wrap',
            }}>
              {['All', 'Before/After', 'Field Work', 'Community'].map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  style={{
                    padding: '7px 22px', borderRadius: 20, border: 'none',
                    background: photoCategory === cat ? '#2e7d32' : '#e8f5e9',
                    color: photoCategory === cat ? '#fff' : '#2e7d32',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Photos grid */}
          {activeTab === 'photos' && (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 16,
              }}>
                {displayedPhotos.map((photo, i) => (
                  <div
                    key={i}
                    onClick={() => openLightbox(i)}
                    style={{
                      position: 'relative',
                      borderRadius: 12,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      aspectRatio: '4/3',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)';
                      const img = e.currentTarget.querySelector('img');
                      if (img) img.style.transform = 'scale(1.08)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                      const img = e.currentTarget.querySelector('img');
                      if (img) img.style.transform = 'scale(1)';
                    }}
                  >
                    <img
                      src={photo.src}
                      alt={photo.caption}
                      loading="lazy"
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease',
                      }}
                    />
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      padding: '24px 14px 12px',
                      color: '#fff', fontSize: 13, fontWeight: 500,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                    }}>
                      <span>{photo.caption}</span>
                      <span style={{
                        background: 'rgba(255,255,255,0.2)', padding: '2px 8px',
                        borderRadius: 10, fontSize: 10, fontWeight: 600,
                      }}>{photo.category}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More */}
              {visibleCount < filteredPhotos.length && (
                <div style={{ textAlign: 'center', marginTop: 36 }}>
                  <button
                    onClick={() => setVisibleCount(prev => prev + PHOTOS_PER_PAGE)}
                    style={{
                      padding: '13px 44px', borderRadius: 30, border: 'none',
                      background: 'linear-gradient(135deg, #2e7d32, #4caf50)',
                      color: '#fff', fontSize: 15, fontWeight: 600,
                      cursor: 'pointer', boxShadow: '0 4px 16px rgba(46,125,50,0.3)',
                      transition: 'all 0.3s',
                    }}
                  >
                    Load More ({filteredPhotos.length - visibleCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}

          {/* Videos grid */}
          {activeTab === 'videos' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
              gap: 24,
            }}>
              {videos.map((video, i) => (
                <div
                  key={i}
                  style={{
                    borderRadius: 16,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    background: '#000',
                  }}
                >
                  <video
                    controls
                    preload="metadata"
                    style={{ width: '100%', display: 'block' }}
                  >
                    <source src={video.src} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div style={{
                    padding: '12px 16px',
                    background: '#fff',
                    fontSize: 14, fontWeight: 600, color: '#333',
                  }}>
                    {video.caption}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && lightbox < filteredPhotos.length && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          {/* Close btn */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute', top: 20, right: 20,
              background: 'none', border: 'none', color: '#fff',
              fontSize: 32, cursor: 'pointer', zIndex: 10,
              width: 44, height: 44, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            &times;
          </button>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + filteredPhotos.length) % filteredPhotos.length); }}
            style={{
              position: 'absolute', left: 20, top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', border: 'none',
              color: '#fff', fontSize: 28, cursor: 'pointer',
              width: 48, height: 48, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            &#8249;
          </button>

          {/* Image */}
          <img
            src={filteredPhotos[lightbox].src}
            alt={filteredPhotos[lightbox].caption}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '75vh',
              objectFit: 'contain', borderRadius: 8,
              cursor: 'default',
            }}
          />

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % filteredPhotos.length); }}
            style={{
              position: 'absolute', right: 20, top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', border: 'none',
              color: '#fff', fontSize: 28, cursor: 'pointer',
              width: 48, height: 48, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            &#8250;
          </button>

          {/* Thumbnail strip */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', bottom: 60, left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', gap: 6, overflowX: 'auto',
              maxWidth: '80vw', padding: 8,
              background: 'rgba(0,0,0,0.5)', borderRadius: 12,
            }}
          >
            {filteredPhotos.slice(0, 20).map((p, i) => (
              <img
                key={i}
                src={p.src}
                alt=""
                onClick={() => setLightbox(i)}
                style={{
                  width: 48, height: 36, objectFit: 'cover',
                  borderRadius: 4, cursor: 'pointer',
                  border: i === lightbox ? '2px solid #ff9800' : '2px solid transparent',
                  opacity: i === lightbox ? 1 : 0.5,
                  transition: 'all 0.2s',
                  flexShrink: 0,
                }}
              />
            ))}
          </div>

          {/* Caption */}
          <div style={{
            position: 'absolute', bottom: 24, left: '50%',
            transform: 'translateX(-50%)',
            color: '#fff', fontSize: 15, fontWeight: 500,
            background: 'rgba(0,0,0,0.5)', padding: '8px 24px',
            borderRadius: 20, whiteSpace: 'nowrap',
          }}>
            {filteredPhotos[lightbox].caption} &mdash; {lightbox + 1} / {filteredPhotos.length}
          </div>
        </div>
      )}
    </>
  );
}
